import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { servicesData } from '../data/mock';
import SEO from '../components/SEO';

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [hoveredService, setHoveredService] = useState(null);

  const handleCategoryClick = (index) => {
    setActiveCategory(index);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Services - Film Production, Digital Marketing, Branding & Consulting"
        description="Explore our comprehensive creative services including film production, wedding cinematography, website development, SEO, branding, graphic design, motion graphics, and strategic consulting."
        keywords="film production services, wedding videography, digital marketing, web development, SEO services, branding, graphic design, motion graphics, consulting, worldwide"
        canonical="https://creativedirectorkhan.com/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Creative Services - CreativeDirectorKhan | KHAN",
          "description": "Comprehensive creative solutions: film production, digital marketing, branding, web development, and consulting.",
          "url": "https://creativedirectorkhan.com/services",
          "mainEntity": {
            "@type": "ItemList",
            "itemListElement": servicesData.flatMap(cat => cat.services).map((svc, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "url": `https://creativedirectorkhan.com/services/${svc.slug}`,
              "name": svc.name
            }))
          }
        }}
      />
      {/* Hero Header */}
      <section className="pt-32 pb-20 px-8 md:px-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-white/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">What I Do</p>
              <h1 className="font-serif text-6xl md:text-8xl lg:text-[140px] font-light text-white leading-[0.85]">
                Services
              </h1>
            </div>
            <p className="text-white/50 text-lg max-w-md leading-relaxed lg:pb-4">
              Comprehensive creative solutions tailored to elevate your brand and captivate your audience.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="border-t border-white/5">
        <div className="flex flex-col lg:flex-row min-h-[80vh]">
          {/* Left Side - Category Navigation */}
          <div className="lg:w-1/3 border-b lg:border-b-0 lg:border-r border-white/5">
            <div className="lg:sticky lg:top-24">
              {servicesData.map((category, index) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(index)}
                  type="button"
                  className={`w-full text-left px-8 md:px-12 py-8 border-b border-white/5 last:border-b-0 transition-all duration-500 group relative overflow-hidden ${
                    activeCategory === index ? 'bg-white/[0.03]' : 'hover:bg-white/[0.02]'
                  }`}
                >
                  {/* Active Indicator */}
                  <div className={`absolute left-0 top-0 bottom-0 w-[2px] bg-white transition-all duration-500 ${
                    activeCategory === index ? 'opacity-100' : 'opacity-0'
                  }`} />
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <span className={`font-serif text-5xl md:text-6xl transition-colors duration-500 ${
                        activeCategory === index ? 'text-white/30' : 'text-white/10 group-hover:text-white/20'
                      }`}>
                        0{index + 1}
                      </span>
                      <h2 className={`font-serif text-2xl md:text-3xl transition-colors duration-500 ${
                        activeCategory === index ? 'text-white' : 'text-white/50 group-hover:text-white/70'
                      }`}>
                        {category.category}
                      </h2>
                    </div>
                    <ArrowRight className={`w-5 h-5 transition-all duration-500 ${
                      activeCategory === index 
                        ? 'text-white/60 translate-x-0 opacity-100' 
                        : 'text-white/30 -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0'
                    }`} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right Side - Services Detail */}
          <div className="lg:w-2/3 relative min-h-[500px] lg:min-h-0">
            {servicesData.map((category, categoryIndex) => (
              <div
                key={category.id}
                className={`transition-all duration-500 ${
                  activeCategory === categoryIndex 
                    ? 'block opacity-100' 
                    : 'hidden opacity-0'
                }`}
              >
                {/* Category Header */}
                <div className="px-8 md:px-16 py-12 border-b border-white/5">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-8 h-px bg-white/30" />
                    <span className="text-white/40 text-xs tracking-[0.3em] uppercase">
                      {category.category} Services
                    </span>
                  </div>
                  <p className="text-white/50 text-lg max-w-lg">
                    {categoryIndex === 0 && 'Professional cinematography and media production services for all your visual storytelling needs.'}
                    {categoryIndex === 1 && 'Digital solutions to enhance your online presence and drive meaningful results.'}
                    {categoryIndex === 2 && 'Creative services that build compelling brand identities and visual experiences.'}
                    {categoryIndex === 3 && 'Strategic consulting for agencies and businesses to optimize media, technology, and operations.'}
                  </p>
                </div>

                {/* Services Grid */}
                <div className="grid md:grid-cols-2">
                  {category.services.map((service, serviceIndex) => (
                    <Link
                      key={serviceIndex}
                      to={`/services/${service.slug}`}
                      onMouseEnter={() => setHoveredService(`${categoryIndex}-${serviceIndex}`)}
                      onMouseLeave={() => setHoveredService(null)}
                      className="group relative border-b border-r border-white/5 last:border-r-0 md:[&:nth-child(2n)]:border-r-0 block"
                    >
                      {/* Hover Background */}
                      <div className={`absolute inset-0 bg-gradient-to-br from-white/[0.05] to-transparent transition-opacity duration-500 ${
                        hoveredService === `${categoryIndex}-${serviceIndex}` ? 'opacity-100' : 'opacity-0'
                      }`} />
                      
                      <div className="relative px-8 md:px-12 py-10 min-h-[200px] flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="font-serif text-2xl md:text-3xl font-light text-white/90 group-hover:text-white transition-colors duration-500">
                              {service.name}
                            </h3>
                            <ArrowUpRight className={`w-5 h-5 text-white/30 transition-all duration-500 ${
                              hoveredService === `${categoryIndex}-${serviceIndex}` 
                                ? 'opacity-100 translate-x-0 -translate-y-0 text-white/60' 
                                : 'opacity-0 translate-x-1 translate-y-1'
                            }`} />
                          </div>
                          <p className="text-white/40 text-sm leading-relaxed group-hover:text-white/50 transition-colors duration-500">
                            {service.description}
                          </p>
                        </div>
                        
                        {/* View Project Link */}
                        <div className="flex items-center gap-2 mt-6">
                          <span className="text-white/[0.06] font-serif text-4xl group-hover:text-white/[0.12] transition-colors duration-500">
                            0{serviceIndex + 1}
                          </span>
                          <span className={`text-xs tracking-[0.2em] uppercase ml-auto transition-all duration-500 ${
                            hoveredService === `${categoryIndex}-${serviceIndex}` 
                              ? 'text-white/60 translate-x-0' 
                              : 'text-transparent -translate-x-4'
                          }`}>
                            View Details
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start mb-20">
            <div>
              <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-4">The Process</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-white">How I Work</h2>
            </div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understanding your vision, goals, and requirements through in-depth consultation.' },
              { step: '02', title: 'Strategy', desc: 'Crafting a tailored approach that aligns with your objectives and timeline.' },
              { step: '03', title: 'Creation', desc: 'Bringing ideas to life with precision, creativity, and attention to detail.' },
              { step: '04', title: 'Delivery', desc: 'Polished results that exceed expectations, delivered on time.' }
            ].map((item, index) => (
              <div key={index} className="group relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 border border-white/5 group-hover:border-white/10 transition-colors duration-500" />
                <div className="relative bg-[#0a0a0a] p-6">
                  <span className="font-serif text-5xl text-white/10 group-hover:text-white/20 transition-colors duration-500 block mb-6">
                    {item.step}
                  </span>
                  <h3 className="font-serif text-xl text-white/90 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-white/40 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-8 md:px-12 text-center">
          <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">Ready to Begin?</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white mb-8">
            Let&apos;s Discuss Your
            <span className="italic text-white/40"> Project</span>
          </h2>
          <p className="text-white/40 mb-14 max-w-md mx-auto">
            Book a consultation to explore how we can bring your vision to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-4 px-12 py-5 border border-white/20 text-white/90 text-sm tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500 group"
          >
            <span>Book Consultation</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
